import { badgeVariants } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PresetControls } from '@/components/common/PresetControls'
import type { NamedPresetControls } from '@/hooks/useNamedPresets'

export type FilterDefaults = {
  presets?: NamedPresetControls
  hasSaved: boolean
  saving: boolean
  error: string | null
  onSave: () => void
  onRestore: () => void
}

export function FilterChip({
  value,
  selected,
  disabled,
  uppercase = false,
  className,
  onClick,
}: {
  value: string
  selected: boolean
  disabled: boolean
  uppercase?: boolean
  className?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      className={cn(
        badgeVariants({ variant: selected ? 'default' : 'outline' }),
        'cursor-pointer font-normal text-xs px-2 py-0.5 disabled:cursor-not-allowed disabled:opacity-50',
        uppercase && 'uppercase',
        className,
      )}
      onClick={onClick}
    >
      {value}
    </button>
  )
}

export function SelectionActions({
  label,
  disabled,
  onAll,
  onNone,
}: {
  label: string
  disabled: boolean
  onAll: () => void
  onNone: () => void
}) {
  return (
    <div className="flex shrink-0 gap-1">
      <button
        type="button"
        aria-label={`Select all ${label}`}
        disabled={disabled}
        onClick={onAll}
        className="px-1 text-[10px] text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        All
      </button>
      <button
        type="button"
        aria-label={`Clear all ${label}`}
        disabled={disabled}
        onClick={onNone}
        className="px-1 text-[10px] text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        None
      </button>
    </div>
  )
}

export function DefaultsFooter({
  label,
  defaults,
  disabled = false,
  className,
}: {
  label: string
  defaults: FilterDefaults
  disabled?: boolean
  className?: string
}) {
  return (
    <div className={cn('space-y-2 border-t border-border pt-3', className)}>
      {defaults.presets && <PresetControls label={`${label} filters`} presets={{
        ...defaults.presets, disabled: defaults.presets.disabled || disabled || defaults.saving,
      }} />}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          className="h-7 text-[11px]"
          aria-label={`Save ${label} filters as default`}
          disabled={disabled || defaults.saving || defaults.presets?.busy}
          onClick={defaults.onSave}
        >
          {defaults.saving ? 'Saving…' : 'Save as default'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-[11px]"
          aria-label={`Restore saved ${label} filters`}
          disabled={disabled || defaults.saving || defaults.presets?.busy || !defaults.hasSaved}
          onClick={defaults.onRestore}
        >
          Restore saved
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground" role="status">
        {defaults.hasSaved
          ? 'Your saved default is applied when you open the screener.'
          : 'Save these filters as your account default.'}
      </p>
      {defaults.error && (
        <p role="alert" className="text-xs text-destructive">{defaults.error}</p>
      )}
    </div>
  )
}
